import React, { useState, useEffect, useRef } from "react";
import { motion, useInView } from "framer-motion";
import {
  ArrowRight,
  Menu,
  X,
  CheckCircle2,
  ChevronDown,
  Zap,
  Shield,
  Globe,
  TrendingUp,
  FileText,
  CreditCard,
  ShoppingCart,
  Receipt,
  Search,
  BarChart3,
  Star,
  Play,
} from "lucide-react";
import { AuroraBackground } from "./components/ui/aurora-background";
import { LogoCloud } from "./components/ui/logo-cloud-3";
import { OrbitingAvatarsCTA } from "./components/ui/orbiting-avatars";
import { ContainerScroll } from "./components/ui/container-scroll-animation";
import { CinematicFooter } from "./components/ui/motion-footer";
import { FeatureCarousel } from "./components/ui/feature-carousel";
import { FlipWords } from "./components/ui/flip-words";
import { TypingEffect } from "./components/ui/typing-effect";
import { ReactComponent as MediusLogo } from "./medius-logo.svg";
import polarisUrl from "./polaris.svg";
import { cn } from "./lib/utils";

// ── Data ──────────────────────────────────────────────────────────────────────


const stats = [
  { value: "3,000+", label: "Companies worldwide" },
  { value: "97%", label: "Invoice auto-match rate" },
  { value: "4×", label: "Faster approvals" },
  { value: "8–12 wks", label: "Average go-live time" },
];

function wordmarkSvg(text: string, color = "#304140"): string {
  const w = Math.round(text.length * 8.8 + 24);
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${w} 22" width="${w}" height="22"><text x="${w / 2}" y="16" text-anchor="middle" font-family="system-ui,-apple-system,sans-serif" font-weight="700" font-size="13.5" letter-spacing="0.3" fill="${color}">${text}</text></svg>`;
  return `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg)}`;
}

const mediusLogos = [
  { src: wordmarkSvg("PUMA"), alt: "PUMA", width: 80, height: 22 },
  { src: wordmarkSvg("Nissan"), alt: "Nissan", width: 80, height: 22 },
  { src: wordmarkSvg("Karl Lagerfeld"), alt: "Karl Lagerfeld", width: 130, height: 22 },
  { src: wordmarkSvg("Lush"), alt: "Lush Cosmetics", width: 55, height: 22 },
  { src: wordmarkSvg("WD-40"), alt: "WD-40", width: 70, height: 22 },
  { src: wordmarkSvg("Cineplex"), alt: "Cineplex", width: 90, height: 22 },
  { src: wordmarkSvg("U-Haul"), alt: "U-Haul", width: 75, height: 22 },
  { src: wordmarkSvg("Viking Line"), alt: "Viking Line", width: 105, height: 22 },
  { src: wordmarkSvg("Matas"), alt: "Matas", width: 80, height: 22 },
  { src: wordmarkSvg("Tony's Chocolonely"), alt: "Tony's Chocolonely", width: 150, height: 22 },
];


const pillars = [
  {
    icon: Zap,
    title: "Agentic AI",
    desc: "Our AI doesn't just suggest — it acts. Medius understands, learns, and executes across your entire invoice-to-pay workflow without human intervention.",
  },
  {
    icon: TrendingUp,
    title: "Fast to value",
    desc: "No lengthy implementations. No expensive consultants. Go live in weeks, not months, and see ROI from day one. Average payback period: 3 months.",
  },
  {
    icon: Shield,
    title: "Fraud protection",
    desc: "Built-in fraud and risk detection protects every payment. Stay compliant with global e-Invoicing mandates automatically, across 50+ countries.",
  },
  {
    icon: Globe,
    title: "Global scale",
    desc: "15 offices across the US, UK, Sweden, France, Norway, Denmark, Netherlands, Australia, Poland, and Tunisia. Multi-currency, multi-entity, and local compliance built in.",
  },
];

const navLinks = ["Products", "Why Medius", "Customers", "Pricing"];

const PROD_NAV = [
  { label: "AP Automation", icon: FileText },
  { label: "Payment Automation", icon: CreditCard },
  { label: "Procurement", icon: ShoppingCart },
  { label: "Expense Management", icon: Receipt },
  { label: "Sourcing", icon: Search },
  { label: "Analytics", icon: BarChart3 },
];

// ── Fade-in wrapper ────────────────────────────────────────────────────────────
function FadeUp({
  children,
  delay = 0,
  className = "",
}: {
  children: React.ReactNode;
  delay?: number;
  className?: string;
}) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-60px" });
  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 28 }}
      animate={inView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.6, delay, ease: "easeOut" }}
      className={className}
    >
      {children}
    </motion.div>
  );
}

// ── Animated stat ─────────────────────────────────────────────────────────────
function AnimatedStat({ value, label }: { value: string; label: string }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true });
  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, scale: 0.85 }}
      animate={inView ? { opacity: 1, scale: 1 } : {}}
      transition={{ duration: 0.5, ease: "backOut" }}
      className="flex flex-col items-center gap-1"
    >
      <span className="text-4xl md:text-5xl font-bold text-white">{value}</span>
      <span className="text-sm text-white/55 text-center font-medium">{label}</span>
    </motion.div>
  );
}

// ── Navbar ────────────────────────────────────────────────────────────────────
function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [productsOpen, setProductsOpen] = useState(false);

  useEffect(() => {
    const handler = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", handler);
    return () => window.removeEventListener("scroll", handler);
  }, []);

  return (
    <>
      <nav
        className={cn(
          "fixed top-0 left-0 right-0 z-50 transition-all duration-300",
          scrolled
            ? "bg-white/90 backdrop-blur-md shadow-sm border-b border-slate-200/60"
            : "bg-transparent"
        )}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <a href="#top" className="flex items-center cursor-pointer">
            <MediusLogo
              className={cn(
                "h-6 w-auto transition-all duration-300",
                scrolled ? "" : "brightness-0 invert"
              )}
            />
          </a>

          <ul className="hidden lg:flex items-center gap-1">
            {navLinks.map((link) =>
              link === "Products" ? (
                <li
                  key="Products"
                  className="relative"
                  onMouseEnter={() => setProductsOpen(true)}
                  onMouseLeave={() => setProductsOpen(false)}
                >
                  <a
                    href="#products"
                    className={cn(
                      "flex items-center gap-1 px-3.5 py-2 rounded-lg text-sm font-medium transition-all duration-200 cursor-pointer",
                      scrolled
                        ? "text-slate-600 hover:text-slate-900 hover:bg-slate-100"
                        : "text-white/80 hover:text-white hover:bg-white/10"
                    )}
                  >
                    Products
                    <ChevronDown className={cn("w-3.5 h-3.5 opacity-60 transition-transform duration-200", productsOpen && "rotate-180")} />
                  </a>
                  {productsOpen && (
                    <div className="absolute top-full left-0 mt-1.5 w-56 bg-white rounded-xl shadow-xl border border-slate-200/80 py-2 z-50">
                      {PROD_NAV.map((item) => {
                        const Icon = item.icon;
                        return (
                          <a
                            key={item.label}
                            href="#products"
                            className="flex items-center gap-3 px-4 py-2.5 text-sm text-slate-600 hover:bg-slate-50 hover:text-slate-900 transition-colors duration-150 cursor-pointer"
                          >
                            <Icon className="w-4 h-4 text-[#DA2028] shrink-0" />
                            {item.label}
                          </a>
                        );
                      })}
                    </div>
                  )}
                </li>
              ) : (
                <li key={link}>
                  <a
                    href={`#${link.toLowerCase().replace(" ", "-")}`}
                    className={cn(
                      "flex items-center gap-1 px-3.5 py-2 rounded-lg text-sm font-medium transition-all duration-200 cursor-pointer",
                      scrolled
                        ? "text-slate-600 hover:text-slate-900 hover:bg-slate-100"
                        : "text-white/80 hover:text-white hover:bg-white/10"
                    )}
                  >
                    {link}
                  </a>
                </li>
              )
            )}
          </ul>

          <div className="hidden lg:flex items-center gap-2">
            <a
              href="#top"
              className={cn(
                "px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 cursor-pointer",
                scrolled
                  ? "text-slate-600 hover:text-slate-900 hover:bg-slate-100"
                  : "text-white/80 hover:text-white hover:bg-white/10"
              )}
            >
              Sign in
            </a>
            <a
              href="#contact"
              className="px-4 py-2 rounded-lg text-sm font-semibold bg-[#DA2028] hover:bg-[#C41B22] text-white shadow-sm transition-all duration-200 cursor-pointer"
            >
              Book a demo
            </a>
          </div>

          <button
            onClick={() => setMobileOpen(!mobileOpen)}
            className={cn(
              "lg:hidden p-2 rounded-lg transition-colors cursor-pointer",
              scrolled ? "text-slate-700 hover:bg-slate-100" : "text-white hover:bg-white/10"
            )}
            aria-label="Toggle menu"
          >
            {mobileOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>
      </nav>

      {mobileOpen && (
        <div className="fixed inset-0 z-40 bg-slate-900/95 backdrop-blur-md lg:hidden">
          <div className="flex flex-col h-full pt-20 px-6 pb-8">
            <ul className="flex flex-col gap-1 flex-1">
              {navLinks.map((link) => (
                <li key={link}>
                  <a
                    href={`#${link.toLowerCase().replace(" ", "-")}`}
                    onClick={() => setMobileOpen(false)}
                    className="block px-4 py-3 rounded-xl text-white text-lg font-medium hover:bg-white/10 transition-colors cursor-pointer"
                  >
                    {link}
                  </a>
                </li>
              ))}
            </ul>
            <div className="flex flex-col gap-3">
              <a href="#top" className="w-full py-3 rounded-xl text-white text-center font-medium border border-white/20 hover:bg-white/10 transition-colors cursor-pointer">
                Sign in
              </a>
              <a href="#contact" className="w-full py-3 rounded-xl bg-[#DA2028] hover:bg-[#C41B22] text-white text-center font-semibold transition-colors cursor-pointer">
                Book a demo
              </a>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

// ── Hero ──────────────────────────────────────────────────────────────────────
function Hero() {
  return (
    <>
      <AuroraBackground
        className="!h-auto min-h-screen !items-start !bg-[#151E1E] text-white"
        showRadialGradient={true}
      >
        <div className="relative z-10 w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-28 pb-16 flex flex-col items-center text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1, duration: 0.6 }}
            className="mb-6"
          >
            <span className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-white/8 border border-white/15 text-white/75 text-sm font-medium backdrop-blur-sm">
              <span className="w-2 h-2 rounded-full bg-red-400 animate-pulse" />
              Gartner Magic Quadrant Leader · 2025
            </span>
          </motion.div>

          <motion.h1
            initial={{ opacity: 0, y: 32 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2, duration: 0.7, ease: "easeOut" }}
            className="text-5xl sm:text-6xl md:text-7xl lg:text-8xl font-bold leading-[1.05] tracking-tight max-w-5xl"
          >
            Autonomous AP,{" "}
            <span className="bg-gradient-to-r from-red-400 via-rose-300 to-red-500 bg-clip-text text-transparent">
              powered by
              <br />
              agentic AI
            </span>
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.35, duration: 0.6 }}
            className="mt-6 text-lg md:text-xl text-slate-300 max-w-2xl leading-relaxed"
          >
            Medius eliminates manual accounts payable work — from invoice capture to
            payment — so your finance team can focus on what actually matters.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5, duration: 0.6 }}
            className="mt-10 flex flex-col sm:flex-row items-center gap-3"
          >
            <a
              href="#contact"
              className="group inline-flex items-center gap-2 px-7 py-3.5 rounded-xl bg-[#DA2028] hover:bg-[#C41B22] text-white font-semibold text-base shadow-lg shadow-red-700/30 transition-all duration-200 cursor-pointer"
            >
              Book a demo
              <ArrowRight className="w-4 h-4 transition-transform duration-200 group-hover:translate-x-1" />
            </a>
            <a
              href="#products"
              className="group inline-flex items-center gap-2 px-7 py-3.5 rounded-xl bg-white/10 hover:bg-white/20 border border-white/20 text-white font-semibold text-base backdrop-blur-sm transition-all duration-200 cursor-pointer"
            >
              <Play className="w-4 h-4" />
              Watch 2-min demo
            </a>
          </motion.div>

        </div>
      </AuroraBackground>

      {/* Scroll-animated platform preview */}
      <div className="bg-[#151E1E] overflow-hidden">
        <ContainerScroll
          titleComponent={
            <motion.p
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2, duration: 0.6 }}
              className="text-slate-500 text-xs font-semibold uppercase tracking-[0.2em] mb-3"
            >
              The Medius Platform
            </motion.p>
          }
        >
          <img
            src={polarisUrl}
            alt="Medius Polaris — AP Automation Platform"
            className="w-full h-full object-cover object-top"
            draggable={false}
          />
        </ContainerScroll>
      </div>
    </>
  );
}

// ── Stats bar ─────────────────────────────────────────────────────────────────
function StatsBar() {
  return (
    <section className="bg-[#304140] py-14">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-8 md:gap-4 md:divide-x-2 divide-white/10">
          {stats.map((s) => (
            <AnimatedStat key={s.label} value={s.value} label={s.label} />
          ))}
        </div>
      </div>
    </section>
  );
}

// ── Products ──────────────────────────────────────────────────────────────────
function Products() {
  return (
    <section className="bg-white py-24 lg:py-32" id="products">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <FadeUp className="text-center mb-14">
          <span className="inline-flex items-center gap-2.5 text-[#DA2028] text-xs font-semibold tracking-widest uppercase mb-4">
            <span className="block w-5 h-px bg-[#DA2028]" />
            Product Suite
          </span>
          <h2 className="text-4xl md:text-5xl font-bold text-slate-900 mb-4">
            Everything finance teams need
          </h2>
          <p className="text-lg text-slate-500 max-w-xl mx-auto">
            One platform. Complete spend visibility from purchase to payment.
          </p>
        </FadeUp>
      </div>
      <FeatureCarousel />
    </section>
  );
}

// ── Why Medius ────────────────────────────────────────────────────────────────
const flipWords = ["automated", "intelligent", "fraud-proof", "effortless"];

function WhyMedius() {
  return (
    <section className="bg-slate-50 min-h-screen flex items-center" id="why-medius">
      <div className="w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24">
        <FadeUp className="text-center mb-16">
          <span className="inline-flex items-center gap-2.5 text-[#DA2028] text-xs font-semibold tracking-widest uppercase mb-4">
            <span className="block w-5 h-px bg-[#DA2028]" />
            Why Medius
          </span>

          <div className="text-4xl md:text-6xl font-bold tracking-tight text-slate-900 leading-[1.08] mb-5">
            Accounts payable,<br />
            finally{" "}
            <FlipWords
              words={flipWords}
              duration={2800}
              className="text-[#DA2028]"
            />
          </div>

          <p className="text-lg text-slate-500 max-w-xl mx-auto">
            We obsess over automation so your finance team can focus on what actually matters.
          </p>
        </FadeUp>

        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-5">
          {pillars.map((pillar, i) => {
            const PillarIcon = pillar.icon;
            return (
              <FadeUp key={pillar.title} delay={i * 0.1}>
                <div className="rounded-2xl p-6 bg-white border border-slate-200 h-full group hover:border-[#DA2028]/25 hover:shadow-lg transition-all duration-300 cursor-default">
                  <div className="w-11 h-11 rounded-xl bg-red-50 flex items-center justify-center mb-4 group-hover:bg-[#DA2028] transition-colors duration-300">
                    <PillarIcon className="w-5 h-5 text-[#DA2028] group-hover:text-white transition-colors duration-300" />
                  </div>
                  <h3 className="text-lg font-bold text-slate-900 mb-2">{pillar.title}</h3>
                  <p className="text-sm text-slate-600 leading-relaxed">{pillar.desc}</p>
                </div>
              </FadeUp>
            );
          })}
        </div>
      </div>
    </section>
  );
}

// ── Social Proof ──────────────────────────────────────────────────────────────
const testimonials = [
  {
    quote: "We looked at a few products and Medius stood out amongst them as one of the top performers. Their customer service was above and beyond.",
    name: "Carrie Craig",
    role: "Director of Enterprise Business Systems, WD-40 Company",
    img: "https://i.pravatar.cc/150?img=11",
  },
  {
    quote: "Before, it felt like we were missing something, but Medius gives us peace of mind. We feel confident handling invoices timely.",
    name: "Jason Mayfield",
    role: "Director of Accounting, NIC Global",
    img: "https://i.pravatar.cc/150?img=32",
  },
];

const orbitingAvatars = [
  { src: "https://i.pravatar.cc/150?img=11", alt: "Carrie Craig" },
  { src: "https://i.pravatar.cc/150?img=32", alt: "Jason Mayfield" },
  { src: "https://i.pravatar.cc/150?img=15", alt: "Finance leader" },
  { src: "https://i.pravatar.cc/150?img=68", alt: "Finance leader" },
  { src: "https://i.pravatar.cc/150?img=25", alt: "Finance leader" },
  { src: "https://i.pravatar.cc/150?img=57", alt: "Finance leader" },
  { src: "https://i.pravatar.cc/150?img=3",  alt: "Finance leader" },
];

const awards = [
  { label: "Gartner Magic Quadrant", sub: "Leader · Accounts Payable Applications 2025" },
  { label: "Ardent Partners", sub: "Market Leader & Elite Performer in AI Innovation · 2026" },
  { label: "The Hackett Group", sub: "SolutionMap Value Leader · 2026" },
];

function SocialProof() {
  const [activeTestimonial, setActiveTestimonial] = useState(0);
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-80px" });

  useEffect(() => {
    const interval = setInterval(() => {
      setActiveTestimonial((prev) => (prev + 1) % testimonials.length);
    }, 5000);
    return () => clearInterval(interval);
  }, []);

  const t = testimonials[activeTestimonial];

  return (
    <section className="bg-[#0E1616] py-24 lg:py-32 overflow-hidden" id="customers">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <FadeUp className="text-center mb-16">
          <span className="inline-flex items-center gap-2.5 text-[#DA2028] text-xs font-semibold tracking-widest uppercase mb-4">
            <span className="block w-5 h-px bg-[#DA2028]" />
            Social Proof
          </span>
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">
            Trusted by finance leaders.<br />
            <span className="bg-gradient-to-r from-red-400 to-rose-300 bg-clip-text text-transparent">
              Recognised by analysts.
            </span>
          </h2>
          <p className="text-lg text-slate-400 max-w-xl mx-auto">
            Industry awards meet real customer results. Here's what the market says about Medius.
          </p>
        </FadeUp>

        <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
          {/* Left: orbiting avatars with central testimonial */}
          <div ref={ref} className="relative flex justify-center">
            <OrbitingAvatarsCTA
              avatars={orbitingAvatars}
              className="w-[520px] h-[520px] max-w-full"
              orbitRadius={14}
              orbitDuration={45}
            >
              {/* Central card */}
              <motion.div
                key={activeTestimonial}
                initial={{ opacity: 0, scale: 0.9 }}
                animate={inView ? { opacity: 1, scale: 1 } : {}}
                transition={{ duration: 0.5, ease: "easeOut" }}
                className="bg-white/5 backdrop-blur-md border border-white/10 rounded-2xl p-6 max-w-[260px] shadow-2xl"
              >
                <div className="flex gap-0.5 mb-3">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} className="w-3.5 h-3.5 text-amber-400 fill-amber-400" />
                  ))}
                </div>
                <p className="text-white/90 text-sm leading-relaxed mb-4 italic">
                  "{t.quote}"
                </p>
                <div className="flex items-center gap-3">
                  <img
                    src={t.img}
                    alt={t.name}
                    className="w-9 h-9 rounded-full object-cover ring-2 ring-[#DA2028]/60"
                  />
                  <div>
                    <div className="text-white text-xs font-semibold">{t.name}</div>
                    <div className="text-slate-400 text-xs">{t.role}</div>
                  </div>
                </div>
              </motion.div>
            </OrbitingAvatarsCTA>

            {/* Dot indicators */}
            <div className="absolute bottom-0 left-1/2 -translate-x-1/2 flex gap-2">
              {testimonials.map((_, i) => (
                <button
                  key={i}
                  onClick={() => setActiveTestimonial(i)}
                  className={cn(
                    "w-2 h-2 rounded-full transition-all duration-300 cursor-pointer",
                    i === activeTestimonial ? "bg-[#DA2028] w-5" : "bg-white/20 hover:bg-white/40"
                  )}
                  aria-label={`Testimonial ${i + 1}`}
                />
              ))}
            </div>
          </div>

          {/* Right: awards + stats */}
          <div className="flex flex-col gap-8">
            <div>
              <h3 className="text-sm font-semibold text-slate-500 uppercase tracking-widest mb-5">
                Industry Recognition
              </h3>
              <div className="flex flex-col gap-3">
                {awards.map((award, i) => (
                  <FadeUp key={award.label} delay={i * 0.1}>
                    <div className="flex items-center gap-4 p-4 rounded-2xl bg-white/5 border border-white/10 hover:border-white/20 hover:bg-white/8 transition-all duration-200 group cursor-default">
                      <div className="w-10 h-10 rounded-xl bg-amber-500/10 border border-amber-500/20 flex items-center justify-center shrink-0 group-hover:bg-amber-500/20 transition-colors duration-200">
                        <Star className="w-5 h-5 text-amber-400 fill-amber-400" />
                      </div>
                      <div>
                        <div className="text-xs text-slate-500 font-medium">{award.label}</div>
                        <div className="text-sm font-bold text-white">{award.sub}</div>
                      </div>
                    </div>
                  </FadeUp>
                ))}
              </div>
            </div>

            <div className="border-t border-white/10 pt-8">
              <h3 className="text-sm font-semibold text-slate-500 uppercase tracking-widest mb-5">
                By the numbers
              </h3>
              <div className="grid grid-cols-2 gap-4">
                {[
                  { value: "3,000+", label: "Companies worldwide", sub: "Across all industries" },
                  { value: "97%", label: "Auto-match rate", sub: "On invoice lines" },
                  { value: "4×", label: "Faster approvals", sub: "vs. manual processing" },
                  { value: "89.4%", label: "Touchless invoices", sub: "Achieved by Chadwell Supply" },
                ].map((stat, i) => (
                  <FadeUp key={stat.label} delay={0.2 + i * 0.1}>
                    <div className="p-4 rounded-xl bg-white/5 border border-white/10">
                      <div className="text-2xl font-bold text-white mb-0.5">{stat.value}</div>
                      <div className="text-xs font-semibold text-slate-300">{stat.label}</div>
                      <div className="text-xs text-slate-500 mt-0.5">{stat.sub}</div>
                    </div>
                  </FadeUp>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

// ── Customer logo strip ───────────────────────────────────────────────────────
function CustomerLogoCloud() {
  return (
    <div className="bg-white overflow-hidden border-t border-slate-100">
      <div className="h-px bg-slate-200 [mask-image:linear-gradient(to_right,transparent,black,transparent)]" />
      <LogoCloud logos={mediusLogos} className="py-5" />
      <div className="h-px bg-slate-200 [mask-image:linear-gradient(to_right,transparent,black,transparent)]" />
    </div>
  );
}

// ── Pricing ───────────────────────────────────────────────────────────────────
const pricingPlans = [
  {
    name: "AP Essentials",
    tagline: "Core AP automation for teams getting started.",
    features: [
      "AP Automation",
      "Multi-way matching",
      "Prepayments & recurring invoices",
      "Medius Capture (invoice ingestion)",
      "Analytics & reporting",
      "Standard support",
      "Unlimited users",
      "1 entity",
    ],
    cta: "Talk to sales",
    ctaHref: "mailto:sales@medius.com",
    highlight: false,
  },
  {
    name: "AP 360",
    tagline: "Full AP intelligence with AI and fraud protection.",
    features: [
      "Everything in AP Essentials",
      "Medius Copilot (agentic AI)",
      "Supplier Conversations",
      "Fraud & Risk Detection",
      "Supplier Portal",
      "Priority support",
      "Unlimited users",
      "Up to 3 entities",
    ],
    cta: "Get a quote",
    ctaHref: "mailto:sales@medius.com",
    highlight: true,
  },
];

const addOns = [
  "Payment Automation",
  "Procurement",
  "Expense Management",
  "Sourcing",
];

function Pricing() {
  return (
    <section className="bg-white py-24 lg:py-32" id="pricing">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        <FadeUp className="text-center mb-16">
          <span className="inline-flex items-center gap-2.5 text-[#DA2028] text-xs font-semibold tracking-widest uppercase mb-4">
            <span className="block w-5 h-px bg-[#DA2028]" />
            Pricing
          </span>
          <h2 className="text-4xl md:text-5xl font-bold text-slate-900 tracking-tight mb-4">
            Two plans. One platform.
          </h2>
          <p className="text-lg text-slate-500 max-w-xl mx-auto">
            Pricing scales with your organisation. No per-user seats, no surprise fees.
          </p>
        </FadeUp>

        {/* Plan cards */}
        <div className="grid md:grid-cols-2 gap-5 mb-10">
          {pricingPlans.map((plan, i) => (
            <FadeUp key={plan.name} delay={i * 0.1}>
              <div
                className={cn(
                  "relative rounded-2xl border p-8 flex flex-col h-full transition-all duration-200",
                  plan.highlight
                    ? "bg-white border-[#DA2028]/25 shadow-2xl ring-2 ring-[#DA2028]/30"
                    : "bg-white border-slate-200 hover:border-slate-300 hover:shadow-lg"
                )}
              >
                {plan.highlight && (
                  <div className="absolute -top-3.5 left-1/2 -translate-x-1/2">
                    <span className="px-4 py-1 rounded-full bg-[#DA2028] text-white text-xs font-bold shadow-md">
                      Most popular
                    </span>
                  </div>
                )}

                <div className="mb-6">
                  <div className={cn("text-sm font-semibold mb-1.5", plan.highlight ? "text-[#DA2028]" : "text-slate-500")}>
                    {plan.name}
                  </div>
                  <p className="text-slate-500 text-sm leading-relaxed">
                    {plan.tagline}
                  </p>
                </div>

                <ul className="flex flex-col gap-2.5 mb-8 flex-1">
                  {plan.features.map((f) => (
                    <li key={f} className="flex items-center gap-2.5 text-sm text-slate-700">
                      <CheckCircle2
                        className="shrink-0"
                        style={{ color: "#DA2028", width: 16, height: 16 }}
                      />
                      {f}
                    </li>
                  ))}
                </ul>

                <a
                  href={plan.ctaHref}
                  className={cn(
                    "w-full py-3 rounded-xl text-sm font-semibold text-center transition-all duration-200 cursor-pointer block",
                    plan.highlight
                      ? "bg-[#DA2028] hover:bg-[#C41B22] text-white shadow-lg shadow-red-700/20"
                      : "bg-slate-100 hover:bg-slate-200 text-slate-900"
                  )}
                >
                  {plan.cta}
                </a>
              </div>
            </FadeUp>
          ))}
        </div>

        {/* Add-ons */}
        <FadeUp delay={0.2}>
          <div className="rounded-2xl border border-slate-200 bg-slate-50 px-8 py-6">
            <p className="text-xs font-semibold text-slate-400 uppercase tracking-widest mb-4">
              Available add-ons
            </p>
            <div className="flex flex-wrap gap-3">
              {addOns.map((addon) => (
                <span
                  key={addon}
                  className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-white border border-slate-200 text-sm text-slate-600 font-medium"
                >
                  <CheckCircle2 className="w-3.5 h-3.5 text-[#DA2028] shrink-0" />
                  {addon}
                </span>
              ))}
            </div>
          </div>
        </FadeUp>
      </div>
    </section>
  );
}

// ── Integrations ──────────────────────────────────────────────────────────────
const erpSystems = [
  "SAP", "Oracle", "Microsoft Dynamics", "NetSuite",
  "Workday", "Infor", "IFS", "Epicor", "Sage", "Unit4", "Visma", "Xero",
];

function Integrations() {
  return (
    <section className="bg-white min-h-screen flex items-center border-t border-slate-100" id="integrations">
      <div className="w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 text-center">
        <FadeUp className="inline-flex items-center gap-2.5 text-[#DA2028] text-xs font-semibold tracking-widest uppercase mb-16">
          <span className="block w-5 h-px bg-[#DA2028]" />
          Integrations
        </FadeUp>

        <FadeUp delay={0.1} className="flex flex-col items-center gap-6">
          <p className="text-xl text-slate-400 font-medium">
            Medius integrates with
          </p>
          <TypingEffect
            texts={erpSystems}
            className="text-5xl md:text-7xl lg:text-8xl text-[#304140]"
            rotationInterval={1800}
            typingSpeed={75}
          />
          <p className="mt-12 text-sm text-slate-400">
            + 50 more ERP, procurement, and financial systems
          </p>
        </FadeUp>
      </div>
    </section>
  );
}

// ── FAQ ───────────────────────────────────────────────────────────────────────
const faqs = [
  {
    q: "How quickly can we go live?",
    a: "Average go-live time is 8–12 weeks. Our dedicated implementation team handles ERP integration, data migration, and end-user training — no heavy internal IT resources needed.",
  },
  {
    q: "Which ERPs does Medius integrate with?",
    a: "We support 50+ ERP systems out of the box — including SAP, Oracle, Microsoft Dynamics 365, NetSuite, Workday, and Infor. Custom connectors are available for legacy systems.",
  },
  {
    q: "How does AI fraud detection work?",
    a: "Medius analyses every invoice against historical supplier patterns, payment behaviour, and global fraud signals. Anomalies are flagged in real time — before any payment is released.",
  },
  {
    q: "Is Medius compliant with e-Invoicing mandates?",
    a: "Yes. We support e-Invoicing compliance in 50+ countries, including the Peppol network, and automatically adapt as new country mandates roll out.",
  },
  {
    q: "What does enterprise pricing look like?",
    a: "Enterprise pricing is based on invoice volume and required modules — not per-user seats. Contact our sales team for a tailored proposal and ROI estimate.",
  },
  {
    q: "How is financial data secured?",
    a: "Medius is SOC 2 Type II certified and ISO 27001 compliant. Data is hosted in regional cloud environments and never shared with third parties.",
  },
];

function FAQ() {
  const [open, setOpen] = useState<number | null>(null);

  return (
    <section className="bg-[#F8FAFB] py-24 lg:py-32">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
        <FadeUp className="text-center mb-14">
          <span className="inline-flex items-center gap-2.5 text-[#DA2028] text-xs font-semibold tracking-widest uppercase mb-4">
            <span className="block w-5 h-px bg-[#DA2028]" />
            FAQ
          </span>
          <h2 className="text-4xl md:text-5xl font-bold text-slate-900 tracking-tight">
            Common questions
          </h2>
        </FadeUp>
        <FadeUp delay={0.1}>
          <div className="flex flex-col divide-y divide-slate-200">
            {faqs.map((faq, i) => (
              <div key={i} className="py-5">
                <button
                  onClick={() => setOpen(open === i ? null : i)}
                  className="w-full flex items-center justify-between gap-4 text-left cursor-pointer group"
                  aria-expanded={open === i}
                >
                  <span className={cn(
                    "text-base font-semibold transition-colors duration-200",
                    open === i ? "text-[#DA2028]" : "text-slate-900 group-hover:text-[#DA2028]"
                  )}>
                    {faq.q}
                  </span>
                  <ChevronDown className={cn(
                    "w-5 h-5 shrink-0 text-slate-400 transition-transform duration-300",
                    open === i && "rotate-180 text-[#DA2028]"
                  )} />
                </button>
                {open === i && (
                  <motion.div
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: "auto" }}
                    transition={{ duration: 0.25, ease: "easeOut" }}
                    className="mt-3 text-slate-500 text-sm leading-relaxed pr-8 overflow-hidden"
                  >
                    {faq.a}
                  </motion.div>
                )}
              </div>
            ))}
          </div>
        </FadeUp>
      </div>
    </section>
  );
}

// ── App ───────────────────────────────────────────────────────────────────────
export default function App() {
  return (
    <div className="font-sans">
      <div className="relative z-10">
        <Navbar />
        <Hero />
        <StatsBar />
        <Products />
        <Integrations />
        <WhyMedius />
        <SocialProof />
        <CustomerLogoCloud />
        <Pricing />
        <FAQ />
      </div>
      <CinematicFooter />
    </div>
  );
}
